/**
 * JobDetail Component
 * Comprehensive job view with galleries, selection requests, documents, and activity
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBriefcase,
  FiImage,
  FiSend,
  FiFileText,
  FiClock,
  FiEdit2,
  FiSave,
  FiX,
  FiPlus,
  FiUploadCloud,
  FiTrash2,
  FiDownload,
  FiClipboard,
  FiUser,
  FiCalendar,
  FiCheckCircle,
  FiAlertCircle,
  FiMail,
  FiPhone,
  FiLock,
  FiEye,
  FiMessageSquare,
  FiLink,
  FiFlag,
  FiPlay,
  FiPause,
  FiCheck,
  FiCamera,
  FiPackage,
  FiStar,
  FiHeart
} from 'react-icons/fi';
import GalleryManager from './GalleryManager';
import SelectionRequestBuilder from './SelectionRequestBuilder';
import BatchUploader from './SharedUploadSession';
import useDetachedUploadRefresh from '../hooks/useDetachedUploadRefresh';
import { useUpload } from '../contexts/UploadContext';
import ImageGrid from './ImageGrid';
import { showToast } from './NotificationTray';
import StorageCard from './workspace/StorageCard';
import AlbumsCard from './workspace/AlbumsCard';
import SelectionsCard from './workspace/SelectionsCard';
import DetailsCard from './workspace/DetailsCard';
import ActivityCard from './workspace/ActivityCard';
import '../styles-clients.css';

const STAGE_CONFIG = {
  intake: { label: 'Intake', color: '#435654', icon: FiClipboard, description: 'Project kickoff' },
  proofs: { label: 'Proofs', color: '#304849', icon: FiCamera, description: 'Initial photos' },
  selection: { label: 'Selection', color: '#3e5c58', icon: FiHeart, description: 'Client picks' },
  retouch: { label: 'Retouch', color: '#3e5c58', icon: FiEdit2, description: 'Editing phase' },
  delivery: { label: 'Delivery', color: '#10b981', icon: FiPackage, description: 'Final handoff' },
  complete: { label: 'Complete', color: '#059669', icon: FiStar, description: 'Job finished' }
};

const STAGES = Object.keys(STAGE_CONFIG);

const JOB_STATUS_OPTIONS = ['active', 'paused', 'completed'];

const ACTIVITY_ICONS = {
  job_created: { icon: FiBriefcase, color: '#304849' },
  job_updated: { icon: FiEdit2, color: '#435654' },
  job_stage_changed: { icon: FiFlag, color: '#3e5c58' },
  gallery_created: { icon: FiImage, color: '#304849' },
  gallery_published: { icon: FiEye, color: '#10b981' },
  gallery_unpublished: { icon: FiEye, color: '#3e5c58' },
  token_created: { icon: FiLink, color: '#304849' },
  token_opened: { icon: FiEye, color: '#3e5c58' },
  token_revoked: { icon: FiX, color: '#ef4444' },
  selection_request_sent: { icon: FiSend, color: '#3e5c58' },
  selection_request_opened: { icon: FiEye, color: '#3e5c58' },
  selection_submitted: { icon: FiCheckCircle, color: '#10b981' },
  selection_locked: { icon: FiLock, color: '#435654' },
  asset_favorited: { icon: FiHeart, color: '#ef4444' },
  asset_unfavorited: { icon: FiHeart, color: '#435654' },
  asset_downloaded: { icon: FiDownload, color: '#304849' },
  comment_added: { icon: FiMessageSquare, color: '#3e5c58' },
  document_uploaded: { icon: FiUploadCloud, color: '#304849' },
  document_deleted: { icon: FiTrash2, color: '#ef4444' },
  note_added: { icon: FiClipboard, color: '#3e5c58' }
};

function formatDate(value) {
  if (!value) return ', ';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatDateTime(value) {
  if (!value) return ', ';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatBytes(bytes) {
  if (!bytes) return ', ';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getActivityIcon(eventType) {
  const config = ACTIVITY_ICONS[eventType] || { icon: FiClock, color: '#435654' };
  const Icon = config.icon;
  return { Icon, color: config.color };
}

function getDaysUntilDue(dueDate) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const now = new Date();
  const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  return diff;
}

// Stage progress indicator - enhanced version
function StageProgress({ currentStage, onStageChange }) {
  const currentIndex = STAGES.indexOf(currentStage);

  return (
    <div className="stage-progress-enhanced">
      {STAGES.map((stage, index) => {
        const config = STAGE_CONFIG[stage];
        const isActive = index === currentIndex;
        const isComplete = index < currentIndex;
        const isFuture = index > currentIndex;
        const StageIcon = config.icon;

        return (
          <button
            key={stage}
            className={`stage-step-enhanced ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${isFuture ? 'future' : ''}`}
            onClick={() => onStageChange(stage)}
            style={{ '--stage-color': config.color }}
            title={config.description}
          >
            <div className="stage-connector" />
            <span className="stage-dot-enhanced">
              {isComplete ? <FiCheckCircle size={18} /> : <StageIcon size={18} />}
            </span>
            <div className="stage-info">
              <span className="stage-label-enhanced">{config.label}</span>
              <span className="stage-desc">{config.description}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Tab navigation
function TabNav({ activeTab, tabs, onChange }) {
  return (
    <nav className="job-tabs">
      {tabs.map(tab => (
        <button
          key={tab.id}
          className={`job-tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.icon && <tab.icon size={16} />}
          {tab.label}
          {tab.count !== undefined && tab.count > 0 && (
            <span className="tab-count">{tab.count}</span>
          )}
        </button>
      ))}
    </nav>
  );
}

// Overview tab content - enhanced
function OverviewTab({ job, editing, draft, setDraft, onSave, onCancel, saving }) {
  if (editing) {
    return (
      <div className="job-overview editing">
        <div className="overview-form">
          <div className="form-group">
            <label>Job Title</label>
            <input
              type="text"
              value={draft.title}
              onChange={e => setDraft(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                value={draft.status}
                onChange={e => setDraft(prev => ({ ...prev, status: e.target.value }))}
              >
                {JOB_STATUS_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={draft.start_date || ''}
                onChange={e => setDraft(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={draft.due_date || ''}
                onChange={e => setDraft(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={draft.description || ''}
              onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Scope, deliverables, timeline details..."
            />
          </div>

          <div className="form-group">
            <label>Internal Notes (not visible to client)</label>
            <textarea
              value={draft.internal_notes || ''}
              onChange={e => setDraft(prev => ({ ...prev, internal_notes: e.target.value }))}
              rows={3}
              placeholder="Private notes for your team..."
            />
          </div>

          <div className="form-group">
            <label>Client Notes (visible to client)</label>
            <textarea
              value={draft.client_notes || ''}
              onChange={e => setDraft(prev => ({ ...prev, client_notes: e.target.value }))}
              rows={3}
              placeholder="Notes to share with the client..."
            />
          </div>

          <div className="form-actions">
            <button onClick={onCancel} className="btn-secondary">
              <FiX /> Cancel
            </button>
            <button onClick={onSave} className="btn-primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysUntil = getDaysUntilDue(job.due_date);

  return (
    <div className="job-overview">
      {/* Quick stats row */}
      <div className="overview-stats-row">
        <div className="stat-card-enhanced">
          <div className="stat-icon" style={{ background: 'rgba(48, 72, 73, 0.1)', color: '#304849' }}>
            <FiImage size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{job.galleries?.length || 0}</span>
            <span className="stat-label">Galleries</span>
          </div>
        </div>
        <div className="stat-card-enhanced">
          <div className="stat-icon" style={{ background: 'rgba(62, 92, 88, 0.1)', color: '#3e5c58' }}>
            <FiSend size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{job.selection_requests?.length || 0}</span>
            <span className="stat-label">Selection Requests</span>
          </div>
        </div>
        <div className="stat-card-enhanced">
          <div className="stat-icon" style={{ background: 'rgba(62, 92, 88, 0.1)', color: '#3e5c58' }}>
            <FiFileText size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{job.documents?.length || 0}</span>
            <span className="stat-label">Documents</span>
          </div>
        </div>
        <div className="stat-card-enhanced">
          <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <FiClipboard size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{job.notes?.length || 0}</span>
            <span className="stat-label">Notes</span>
          </div>
        </div>
      </div>

      {/* Main info cards */}
      <div className="overview-grid-enhanced">
        <div className="overview-card-enhanced details-card">
          <h4><FiCalendar size={16} /> Timeline & Status</h4>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`detail-value status-badge-inline status-${job.status}`}>
                {job.status === 'active' && <FiPlay size={12} />}
                {job.status === 'paused' && <FiPause size={12} />}
                {job.status === 'completed' && <FiCheck size={12} />}
                {job.status}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Current Stage</span>
              <span className="detail-value stage-badge" style={{ '--stage-color': STAGE_CONFIG[job.stage || 'intake'].color }}>
                {STAGE_CONFIG[job.stage || 'intake'].label}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Start Date</span>
              <span className="detail-value">{formatDate(job.start_date)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Due Date</span>
              <span className={`detail-value ${daysUntil !== null && daysUntil < 0 ? 'overdue' : daysUntil !== null && daysUntil <= 7 ? 'soon' : ''}`}>
                {formatDate(job.due_date)}
                {daysUntil !== null && daysUntil < 0 && <span className="due-warning"> (Overdue)</span>}
                {daysUntil !== null && daysUntil >= 0 && daysUntil <= 7 && <span className="due-soon"> ({daysUntil}d left)</span>}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Created</span>
              <span className="detail-value">{formatDate(job.created_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">{formatDateTime(job.updated_at)}</span>
            </div>
          </div>
        </div>

        <div className="overview-card-enhanced description-card">
          <h4><FiFileText size={16} /> Description</h4>
          <p className="description-text">{job.description || 'No description provided. Click Edit to add project details, scope, and deliverables.'}</p>
        </div>

        {job.internal_notes && (
          <div className="overview-card-enhanced notes-card internal">
            <h4><FiLock size={16} /> Internal Notes</h4>
            <p>{job.internal_notes}</p>
          </div>
        )}

        {job.client_notes && (
          <div className="overview-card-enhanced notes-card client">
            <h4><FiUser size={16} /> Client Notes</h4>
            <p>{job.client_notes}</p>
          </div>
        )}
      </div>

      {/* Recent activity preview */}
      {job.activity && job.activity.length > 0 && (
        <div className="overview-card-enhanced activity-preview">
          <h4><FiClock size={16} /> Recent Activity</h4>
          <div className="activity-preview-list">
            {job.activity.slice(0, 5).map(item => {
              const { Icon, color } = getActivityIcon(item.event_type);
              return (
                <div key={item.id} className="activity-preview-item">
                  <div className="activity-icon-small" style={{ background: `${color}15`, color }}>
                    <Icon size={14} />
                  </div>
                  <div className="activity-preview-content">
                    <span className="activity-text">{item.event_summary || item.event_type.replace(/_/g, ' ')}</span>
                    <span className="activity-time">{formatDateTime(item.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Documents tab content
function DocumentsTab({ job, onUpload, onDelete, uploading }) {
  const MAX_DOC_MB = 25;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_DOC_MB * 1024 * 1024) {
        alert(`File exceeds ${MAX_DOC_MB} MB limit.`);
        e.target.value = '';
        return;
      }
      onUpload(file, () => { e.target.value = ''; });
    }
  };

  return (
    <div className="job-documents">
      <div className="documents-header">
        <h4>Documents</h4>
        <label className="upload-btn">
          <FiUploadCloud /> Upload Document
          <input
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
        </label>
      </div>

      <p className="documents-hint">Max file size: {MAX_DOC_MB} MB</p>

      {!job.documents || job.documents.length === 0 ? (
        <div className="documents-empty">
          <FiFileText size={48} />
          <p>No documents uploaded yet</p>
        </div>
      ) : (
        <div className="documents-list">
          {job.documents.map(doc => (
            <div key={doc.id} className="document-row">
              <div className="document-info">
                <FiFileText size={20} />
                <div>
                  <strong>{doc.original_filename || doc.filename}</strong>
                  <span>{formatBytes(doc.bytes)} · Uploaded {formatDateTime(doc.uploaded_at)}</span>
                </div>
              </div>
              <div className="document-actions">
                {doc.download_url && (
                  <a href={doc.download_url} target="_blank" rel="noopener noreferrer" className="btn-icon">
                    <FiDownload />
                  </a>
                )}
                <button onClick={() => onDelete(doc)} className="btn-icon danger">
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Project uploads tab
function UploadsTab({ jobId, uploadsAlbum, onJobRefresh }) {
  const uploadContext = useUpload();
  const [albumInfo, setAlbumInfo] = useState(uploadsAlbum || null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [error, setError] = useState(null);
  const [showUploader, setShowUploader] = useState(false);

  useEffect(() => {
    setAlbumInfo(uploadsAlbum || null);
  }, [uploadsAlbum?.id]);

  const loadAlbum = async (albumId, forceRefresh = false) => {
    if (!albumId) return;
    try {
      setLoading(true);
      // Add cache-busting param when forcing refresh to bypass Service Worker cache
      const url = forceRefresh ? `/api/albums/${albumId}?_t=${Date.now()}` : `/api/albums/${albumId}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load uploads album');
      const data = await res.json();
      setAlbumInfo(data.album || uploadsAlbum || null);
      setImages(Array.isArray(data.images) ? data.images : []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (albumInfo?.id) {
      loadAlbum(albumInfo.id);
    } else {
      setImages([]);
    }
  }, [albumInfo?.id]);

  const ensureAlbum = async () => {
    if (albumInfo?.id) return albumInfo;
    try {
      setEnsuring(true);
      const res = await fetch(`/api/jobs/${jobId}/uploads/album`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to create uploads album');
      const data = await res.json();
      if (data?.album) {
        setAlbumInfo(data.album);
        await loadAlbum(data.album.id);
        if (onJobRefresh) onJobRefresh();
        return data.album;
      }
      throw new Error('Uploads album missing');
    } catch (err) {
      setError(err.message || 'Failed to create uploads album');
      return null;
    } finally {
      setEnsuring(false);
    }
  };

  const handleStartUpload = async () => {
    const album = await ensureAlbum();
    if (album?.id) {
      setShowUploader(true);
    }
  };

  const handleUploadComplete = async (counts = {}) => {
    if (albumInfo?.id) {
      await loadAlbum(albumInfo.id, true); // Force refresh to bypass SW cache
    }
    if (onJobRefresh) onJobRefresh();
    if (counts.hasProcessing) {
      showToast('Uploads complete', 'Your library will update automatically while previews and metadata finish processing.', 'info');
    }
    if (counts.autoClose) {
      setShowUploader(false);
    }
  };

  return (
    <div className="job-uploads">
      <div className="uploads-header">
        <div>
          <h4>Project Uploads</h4>
          <p className="uploads-subtitle">
            Upload photos directly into this project. These stay out of your main library and live in a dedicated album.
          </p>
        </div>
        <div className="uploads-actions">
          {albumInfo?.id && (
            <Link to={`/albums/${albumInfo.id}`} className="btn-secondary">
              <FiImage /> Open Album
            </Link>
          )}
          <button onClick={handleStartUpload} className="btn-primary" disabled={ensuring}>
            <FiUploadCloud /> {ensuring ? 'Preparing...' : 'Upload Photos'}
          </button>
        </div>
      </div>

      {error && (
        <div className="uploads-error">
          <FiAlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {showUploader && albumInfo?.id && (
        <div className="uploads-uploader" onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); if (uploadContext?.uploading) uploadContext.minimizePanel(); else setShowUploader(false); } }} style={{display: uploadContext?.panelMinimized ? "none" : undefined}}>
          <BatchUploader
            albumId={albumInfo.id}
            libraryScope="client_upload"
            onUploadComplete={handleUploadComplete}
            onRequestClose={() => setShowUploader(false)}
          />
        </div>
      )}

      {loading ? (
        <div className="uploads-loading">Loading uploads...</div>
      ) : images.length === 0 ? (
        <div className="uploads-empty">
          <FiUploadCloud size={48} />
          <p>No project uploads yet</p>
          <span>Upload photos here to keep them tied to this job.</span>
        </div>
      ) : (
        <div className="uploads-grid">
          <ImageGrid images={images} albumId={albumInfo?.id} showProcessingToast />
        </div>
      )}
    </div>
  );
}

// Notes tab content
function NotesTab({ job, onAddNote, saving }) {
  const [noteDraft, setNoteDraft] = useState('');

  const handleAdd = () => {
    if (noteDraft.trim()) {
      onAddNote(noteDraft);
      setNoteDraft('');
    }
  };

  return (
    <div className="job-notes">
      <div className="note-composer">
        <textarea
          value={noteDraft}
          onChange={e => setNoteDraft(e.target.value)}
          placeholder="Add a private note (only visible to your team)..."
          rows={3}
        />
        <button onClick={handleAdd} disabled={saving || !noteDraft.trim()} className="btn-primary">
          <FiPlus /> Add Note
        </button>
      </div>

      {!job.notes || job.notes.length === 0 ? (
        <div className="notes-empty">
          <FiClipboard size={48} />
          <p>No notes yet</p>
        </div>
      ) : (
        <div className="notes-list">
          {job.notes.map(note => (
            <div key={note.id} className="note-item">
              <p>{note.note}</p>
              <span className="note-date">{formatDateTime(note.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Activity tab content - enhanced with colored icons
function ActivityTab({ activity }) {
  if (!activity || activity.length === 0) {
    return (
      <div className="job-activity empty">
        <FiClock size={48} />
        <p>No activity recorded yet</p>
      </div>
    );
  }

  return (
    <div className="job-activity">
      <div className="activity-timeline-enhanced">
        {activity.map((item, index) => {
          const { Icon, color } = getActivityIcon(item.event_type);
          const isLast = index === activity.length - 1;

          return (
            <div key={item.id} className={`activity-item-enhanced ${isLast ? 'last' : ''}`}>
              <div className="activity-line" />
              <div className="activity-icon-enhanced" style={{ background: `${color}15`, color, borderColor: color }}>
                <Icon size={16} />
              </div>
              <div className="activity-content-enhanced">
                <p className="activity-summary">{item.event_summary || item.event_type.replace(/_/g, ' ')}</p>
                <span className="activity-meta">
                  {formatDateTime(item.created_at)}
                  {item.actor_type === 'client' && <span className="actor-badge client">Client</span>}
                  {item.actor_type === 'staff' && <span className="actor-badge staff">Staff</span>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main JobDetail component
export default function JobDetail() {
  const uploadContext = useUpload();
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('workspace');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [annotationStats, setAnnotationStats] = useState({ total: 0, pending: 0 });
  const [galleryAnnotationStats, setGalleryAnnotationStats] = useState({});
  const [showUploader, setShowUploader] = useState(false);
  const [showGalleries, setShowGalleries] = useState(false);

  useEffect(() => {
    const reopen = () => { if (showUploader) setActiveTab('workspace'); };
    window.addEventListener('photos:reopen-uploader', reopen);
    return () => window.removeEventListener('photos:reopen-uploader', reopen);
  }, [showUploader]);

  useEffect(() => {
    loadJob();
  }, [jobId]);

  useDetachedUploadRefresh(detail => detail.libraryScope === 'client_upload' && String(detail.albumId) === String(job?.uploads_album?.id), () => loadJob());

  const loadJob = async () => {
    try {
      if (!job) setLoading(true);
      setError(null);

      const res = await fetch(`/api/jobs/${jobId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load job');

      const data = await res.json();

      // API returns { job, galleries, selection_requests, documents, notes, activity }
      // Flatten into a single object for easier access
      const jobData = {
        ...data.job,
        galleries: data.galleries || [],
        selection_requests: data.selection_requests || [],
        documents: data.documents || [],
        notes: data.notes || [],
        activity: data.activity || [],
        uploads_album: data.uploads_album || null,
        workspace_stats: data.workspace_stats || null,
        // Customer info from join
        customer: data.job?.customer_name ? {
          name: data.job.customer_name,
          email: data.job.customer_email
        } : null
      };

      setJob(jobData);
      setDraft({
        title: jobData.title || '',
        status: jobData.status || 'active',
        description: jobData.description || '',
        start_date: jobData.start_date?.split('T')[0] || '',
        due_date: jobData.due_date?.split('T')[0] || '',
        internal_notes: jobData.internal_notes || '',
        client_notes: jobData.client_notes || ''
      });

      // Load annotation stats for all galleries (in parallel)
      if (jobData.galleries?.length > 0) {
        const results = await Promise.allSettled(
          jobData.galleries.map(gallery =>
            fetch(`/api/annotations/gallery/${gallery.id}`, { credentials: 'include' })
              .then(res => res.ok ? res.json() : null)
              .then(data => ({ galleryId: gallery.id, stats: data?.stats || { total: 0, pending: 0 } }))
          )
        );

        let totalAnnotations = 0;
        let pendingAnnotations = 0;
        const perGallery = {};
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value) {
            const { galleryId, stats } = result.value;
            perGallery[galleryId] = stats;
            totalAnnotations += stats.total || 0;
            pendingAnnotations += stats.pending || 0;
          }
        }
        setAnnotationStats({ total: totalAnnotations, pending: pendingAnnotations });
        setGalleryAnnotationStats(perGallery);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(`/api/jobs/${jobId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(draft)
      });

      if (!res.ok) throw new Error('Failed to update job');

      await loadJob();
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      const res = await fetch(`/api/jobs/${jobId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stage: newStage })
      });

      if (!res.ok) throw new Error('Failed to update stage');

      await loadJob();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUploadDoc = async (file, resetInput) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`/api/jobs/${jobId}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) throw new Error('Failed to upload document');

      await loadJob();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (resetInput) resetInput();
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!confirm(`Delete "${doc.original_filename || doc.filename}"?`)) return;

    try {
      const res = await fetch(`/api/jobs/${jobId}/documents/${doc.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete document');

      await loadJob();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNote = async (note) => {
    try {
      setSaving(true);
      const res = await fetch(`/api/jobs/${jobId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note })
      });

      if (!res.ok) throw new Error('Failed to add note');

      await loadJob();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = useMemo(() => [
    { id: 'workspace', label: 'Workspace', icon: FiImage, count: (job?.galleries?.length || 0) + (job?.uploads_album?.image_count || 0) },
    { id: 'selections', label: 'Selections', icon: FiSend, count: job?.selection_requests?.length || 0 },
    { id: 'details', label: 'Details', icon: FiFileText, count: (job?.documents?.length || 0) + (job?.notes?.length || 0) },
    { id: 'activity', label: 'Activity', icon: FiClock }
  ], [job, annotationStats]);

  if (loading) {
    return (
      <div className="job-detail-page loading">
        <div className="spinner" />
        <span>Loading job...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-detail-page error">
        <FiAlertCircle size={48} />
        <h2>Error Loading Job</h2>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn-secondary">
          <FiArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-page not-found">
        <FiBriefcase size={48} />
        <h2>Job Not Found</h2>
        <button onClick={() => navigate(-1)} className="btn-secondary">
          <FiArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const currentStageConfig = STAGE_CONFIG[job.stage || 'intake'];
  const daysUntil = getDaysUntilDue(job.due_date);

  return (
    <div className="job-detail-page">
      {/* Hero header */}
      <header className="job-hero">
        <div className="job-hero-nav">
          <button onClick={() => navigate(-1)} className="back-btn">
            <FiArrowLeft />
          </button>
          <div className="job-breadcrumb">
            <Link to="/submissions" className="breadcrumb-link">Jobs</Link>
            <span className="breadcrumb-sep">/</span>
            {job.customer && (
              <>
                <Link to="/submissions" className="breadcrumb-link">{job.customer.name}</Link>
                <span className="breadcrumb-sep">/</span>
              </>
            )}
            <span className="breadcrumb-current">{job.title}</span>
          </div>
        </div>

        <div className="job-hero-main">
          <div className="job-hero-info">
            <div className="job-hero-title-row">
              <h1>{job.title}</h1>
              <span className={`job-status-badge status-${job.status}`}>
                {job.status === 'active' && <FiPlay size={12} />}
                {job.status === 'paused' && <FiPause size={12} />}
                {job.status === 'completed' && <FiCheck size={12} />}
                {job.status}
              </span>
            </div>

            {job.customer && (
              <div className="job-hero-customer">
                <FiUser size={14} />
                <span>{job.customer.name}</span>
                {job.customer.email && (
                  <>
                    <span className="hero-sep">·</span>
                    <FiMail size={14} />
                    <span>{job.customer.email}</span>
                  </>
                )}
                {job.customer.phone && (
                  <>
                    <span className="hero-sep">·</span>
                    <FiPhone size={14} />
                    <span>{job.customer.phone}</span>
                  </>
                )}
              </div>
            )}

            <div className="job-hero-meta">
              <div className="meta-item">
                <FiCalendar size={14} />
                <span>Started {formatDate(job.start_date)}</span>
              </div>
              <div className="meta-item stage-pill" style={{ '--stage-color': currentStageConfig.color }}>
                {React.createElement(currentStageConfig.icon, { size: 14 })}
                <span>{currentStageConfig.label}</span>
              </div>
              {job.due_date && (
                <div className={`meta-item ${daysUntil !== null && daysUntil < 0 ? 'overdue' : daysUntil !== null && daysUntil <= 7 ? 'soon' : ''}`}>
                  <FiClock size={14} />
                  <span>
                    Due {formatDate(job.due_date)}
                    {daysUntil !== null && daysUntil < 0 && <strong> (Overdue)</strong>}
                    {daysUntil !== null && daysUntil >= 0 && daysUntil <= 7 && <strong> ({daysUntil}d left)</strong>}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="job-hero-actions">
            {editing ? (
              <>
                <button onClick={() => setEditing(false)} className="btn-secondary">
                  <FiX /> Cancel
                </button>
                <button onClick={handleSave} className="btn-primary" disabled={saving}>
                  <FiSave /> {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="btn-secondary">
                <FiEdit2 /> Edit Job
              </button>
            )}
          </div>
        </div>

        {/* Stage progress */}
        <StageProgress
          currentStage={job.stage || 'intake'}
          onStageChange={handleStageChange}
        />
      </header>

      {/* Tabs */}
      <TabNav activeTab={activeTab} tabs={tabs} onChange={setActiveTab} />

      {/* Tab content */}
      <main className="job-detail-content">
        {(activeTab === 'workspace' || showUploader) && (
          <div className="workspace-overview" style={{display: activeTab === 'workspace' ? undefined : 'none'}}>
            <div className="workspace-grid">
              <StorageCard
                stats={job.workspace_stats}
                uploadsAlbum={job.uploads_album}
                onUploadClick={() => { uploadContext?.maximizePanel(); setShowUploader(true); }}
              />
              <AlbumsCard
                galleries={job.galleries}
                onManageClick={() => setShowGalleries(v => !v)}
                onGalleryClick={(g) => navigate(`/clients/galleries/${g.id}`)}
              />
              <SelectionsCard
                selectionRequests={job.selection_requests}
                onManageClick={() => setActiveTab('selections')}
              />
              <DetailsCard
                job={job}
                documents={job.documents}
                notes={job.notes}
                onEditClick={() => { setEditing(true); setActiveTab('details'); }}
              />
              <ActivityCard
                activity={job.activity}
                onViewAllClick={() => setActiveTab('activity')}
              />
            </div>

            {showUploader && (
              <UploadsTab
                jobId={job.id}
                uploadsAlbum={job.uploads_album}
                onJobRefresh={loadJob}
              />
            )}

            {showGalleries && (
              <GalleryManager
                jobId={job.id}
                customerId={job.customer_id}
                onGalleryChange={loadJob}
                uploadsAlbumId={job.uploads_album?.id}
                annotationStats={galleryAnnotationStats}
              />
            )}
          </div>
        )}

        {activeTab === 'selections' && (
          <SelectionRequestBuilder
            jobId={job.id}
            customerId={job.customer_id}
            galleries={job.galleries || []}
            onRequestChange={loadJob}
          />
        )}

        {activeTab === 'details' && (
          <div className="details-tab">
            {/* Overview info */}
            <OverviewTab
              job={job}
              editing={editing}
              draft={draft}
              setDraft={setDraft}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
              saving={saving}
            />

            {/* Documents */}
            <DocumentsTab
              job={job}
              onUpload={handleUploadDoc}
              onDelete={handleDeleteDoc}
              uploading={uploading}
            />

            {/* Notes */}
            <NotesTab
              job={job}
              onAddNote={handleAddNote}
              saving={saving}
            />
          </div>
        )}

        {activeTab === 'activity' && (
          <ActivityTab activity={job.activity} />
        )}
      </main>
    </div>
  );
}
